/*
 * Figma `118:151` — SELL-03 · Organizer LINE OA · Connection + Verify.
 *
 * `SafetyTemplate`: health, then consequence, then configuration, then audit. The order is the
 * point. Someone opens this screen because something is not working, and the first thing they need
 * is which part is broken — not a form, and certainly not a switch they can throw before they know
 * what it costs.
 *
 * The frame shows more than the old panel did, and all of it turns out to be backed already:
 * `LineOaConnectionDto` has carried `loginChannelId`, `liffId` and `secretsConfigured.loginSecret`
 * all along, and `lineOaApi.audit` has carried the history. The panel simply never drew them, so
 * "LINE Login is not configured" was a thing the server knew and the organizer could not see.
 *
 * Secrets use `CredentialReveal` with no `reveal`. That is the honest rendering: the server has no
 * endpoint that returns a secret and never will, so the component shows configured-or-not and a
 * rotation field, and offers no button that would imply otherwise.
 */
import { useEffect } from 'react';
import {
  Alert, AuditTimeline, Button, Card, CredentialReveal, ReadinessCard, ReadinessRow,
  SafetyTemplate, SaveBar, StatusBadge, TextField, Textarea, Toggle,
} from '..';
import type { AuditEvent, ReadinessTone, SaveState } from '..';
import type { Destination } from '../destinations';
import type { LineOaCheckDto, LineOaConnectionDto, LineOaStatusDto } from '../../api';
import type { LineOaState } from './useLineOa';
import { useLineOa } from './useLineOa';
import { LineSalesReportPanel } from './LineSalesReportPanel';

const STATUS_LABEL: Record<LineOaStatusDto, string> = {
  draft: 'ร่าง — ยังกรอกไม่ครบ',
  verification_pending: 'รอตรวจสอบการเชื่อมต่อ',
  active: 'ใช้งานอยู่',
  verification_failed: 'ตรวจสอบไม่ผ่าน',
  disabled: 'ปิดใช้งานอยู่',
  credential_invalid: 'credential ใช้ไม่ได้แล้ว',
};

const STATUS_TONE: Record<LineOaStatusDto, 'success' | 'warning' | 'danger' | 'neutral'> = {
  draft: 'neutral',
  verification_pending: 'warning',
  active: 'success',
  verification_failed: 'danger',
  disabled: 'neutral',
  credential_invalid: 'danger',
};

const FIELD_LABEL: Record<string, string> = {
  displayName: 'ชื่อที่แสดง',
  basicId: 'LINE Basic ID',
  addFriendUrl: 'ลิงก์เพิ่มเพื่อน',
  messagingChannelId: 'Messaging Channel ID',
  messagingSecret: 'Channel secret',
  messagingAccessToken: 'Channel access token',
  deploymentSecrets: 'secret ฝั่ง deployment',
};

const CHECK_LABEL: Record<LineOaCheckDto['id'], string> = {
  identifiers: 'รูปแบบข้อมูล',
  credential: 'credential ใช้งานได้',
  consistency: 'ตรงกับ OA ที่ LINE ตอบ',
  webhook: 'Webhook',
  login_channel: 'LINE Login',
};

/** '–' is "not applicable here" and '?' is "cannot be checked automatically". Neither is a failure. */
const CHECK_MARK: Record<LineOaCheckDto['status'], string> = {
  passed: '✓', failed: '✕', skipped: '–', unverified: '?',
};

function readiness(connection: LineOaConnectionDto | null): {
  title: string; status: string; tone: ReadinessTone; detail?: string;
}[] {
  if (!connection) {
    return [
      { title: 'Messaging API', status: 'ยังไม่ได้เชื่อมต่อ', tone: 'unknown' },
      { title: 'LINE Login', status: 'ยังไม่ได้เชื่อมต่อ', tone: 'unknown' },
      { title: 'ปุ่มเพิ่มเพื่อน', status: 'ยังไม่ได้เชื่อมต่อ', tone: 'unknown' },
    ];
  }
  const messagingOn = connection.capability === 'messaging';
  const messagingReady = messagingOn
    && !!connection.messagingChannelId
    && connection.secretsConfigured.messagingSecret
    && connection.secretsConfigured.messagingAccessToken;

  return [
    {
      title: 'Messaging API',
      status: !messagingOn ? 'ไม่ได้เปิดใช้' : messagingReady ? 'พร้อม' : 'ยังตั้งค่าไม่ครบ',
      tone: !messagingOn ? 'unknown' : messagingReady ? 'ready' : 'attention',
      detail: messagingOn && !messagingReady
        ? 'ต้องมีทั้ง Channel ID, secret และ access token'
        : undefined,
    },
    {
      title: 'LINE Login',
      status: connection.loginChannelId
        ? connection.secretsConfigured.loginSecret ? 'พร้อม' : 'ยังไม่มี secret'
        : 'ยังไม่ได้ตั้งค่า',
      tone: connection.loginChannelId
        ? connection.secretsConfigured.loginSecret ? 'ready' : 'attention'
        : 'unknown',
      detail: connection.liffId ? `LIFF ${connection.liffId}` : 'ยังไม่มี LIFF ID',
    },
    {
      title: 'ปุ่มเพิ่มเพื่อน',
      status: connection.addFriendUrl ? 'มีลิงก์แล้ว' : 'ยังไม่มีลิงก์',
      tone: connection.addFriendUrl ? 'ready' : 'attention',
      detail: connection.botUserId ? `bot ${connection.botUserId.slice(0, 10)}…` : undefined,
    },
  ];
}

function auditEvents(entries: NonNullable<LineOaState['audit']>): AuditEvent[] {
  return entries.map((entry) => ({
    id: entry.id,
    actor: entry.actor,
    action: entry.action,
    at: new Date(entry.createdAt).toLocaleString('th-TH'),
    note: entry.detail || undefined,
  }));
}

export function LineOaScreen({ destination, state, permitted, role, token = '', eventSlug }: {
  destination: Destination;
  state: LineOaState;
  permitted: boolean;
  role?: string | null;
  token?: string;
  eventSlug?: string;
}) {
  const {
    view, loading, busy, error, loadError, report, audit,
    draft, setDraft, wantsMessaging, setWantsMessaging, secrets, setSecrets,
    message, setMessage, dirty, create, save, rotate, verify, setEnabled, saveSettings, loadAudit,
  } = state;

  /*
   * The history is fetched when the screen is shown rather than with the connection: it is evidence
   * for an investigation, not something the configuration form needs in order to render.
   *
   * Only once a connection exists. `/audit` resolves the event's own connection first and answers
   * 404 when there is none, so asking for the history of a LINE OA nobody has set up was a request
   * that could only fail — swallowed by the hook, and so invisible except as a 404 on every open of
   * this screen for every event without one. A 404 nobody reads is a 404 that hides the next one.
   */
  useEffect(() => {
    if (view?.connection && permitted) void loadAudit();
  }, [view, permitted]); // eslint-disable-line react-hooks/exhaustive-deps

  const connection = view?.connection ?? null;
  const saveState: SaveState = error ? 'error' : busy ? 'saving' : dirty ? 'dirty' : 'clean';
  const messageDirty = message !== (view?.settings.message ?? '');

  return (
    <SafetyTemplate
      gate={{ destination, loading, error: loadError || null, permitted, role }}
      health={
        <>
          <ReadinessRow label="ความพร้อมของ LINE OA">
            {readiness(connection).map((card) => <ReadinessCard key={card.title} {...card} />)}
          </ReadinessRow>
          {connection && (
            <Card
              title="สถานะการเชื่อมต่อ"
              action={
                <div className="jt-inline-actions">
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => void verify()}>
                    ตรวจสอบการเชื่อมต่อ
                  </Button>
                  <Button
                    size="sm"
                    variant={connection.status === 'disabled' ? 'primary' : 'ghost'}
                    disabled={busy}
                    onClick={() => setEnabled(connection.status === 'disabled')}
                  >
                    {connection.status === 'disabled' ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                  </Button>
                </div>
              }
            >
              <div className="jt-inline-actions">
                <StatusBadge tone={STATUS_TONE[connection.status]}>{STATUS_LABEL[connection.status]}</StatusBadge>
                <span className="jt-field-hint">
                  {connection.lastVerifiedAt
                    ? `ตรวจสอบล่าสุด ${new Date(connection.lastVerifiedAt).toLocaleString('th-TH')}`
                    : 'ยังไม่เคยตรวจสอบ'}
                  {' · '}environment {view?.environment ?? '—'}
                  {' · '}การส่งตั๋วผ่าน JamTick LINE OA{' '}
                  {view?.platform.ready ? 'พร้อมใช้งาน' : 'ยังไม่เปิดใช้งาน'}
                </span>
              </div>
              {connection.missingFields.length > 0 && (
                <Alert tone="warning" title="ยังกรอกไม่ครบ">
                  {connection.missingFields.map((f) => FIELD_LABEL[f] ?? f).join(', ')}
                </Alert>
              )}
              {connection.lastVerificationError && (
                <Alert tone="danger" title="ผลตรวจสอบล่าสุดไม่ผ่าน">{connection.lastVerificationError}</Alert>
              )}
              {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}
            </Card>
          )}
        </>
      }
      consequence={
        connection && (
          <Card title="สิ่งที่ลูกค้าจะเห็น" sub="ปุ่มนี้อยู่ท้ายหน้าสถานะคำสั่งซื้อ — ไม่เกี่ยวกับการส่งตั๋ว และลูกค้าที่ไม่กดก็ได้ตั๋วตามปกติ">
            <Toggle
              label="แสดงปุ่มเพิ่มเพื่อน OA ของผู้จัด"
              checked={!!view?.settings.addFriendEnabled}
              disabled={busy || connection.status !== 'active'}
              onChange={(e) => saveSettings({ addFriendEnabled: e.target.checked })}
              consequence={connection.status !== 'active'
                ? 'เปิดได้เมื่อตรวจสอบการเชื่อมต่อผ่านแล้ว'
                : 'ลูกค้าทุกคนที่ชำระเงินสำเร็จจะเห็นปุ่มนี้'}
            />
            <Textarea
              label="ข้อความประกอบปุ่ม"
              limit={280}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              hint="เช่น ติดตามข่าวสารและของแถมได้ที่ LINE ของเรา"
            />
            <div className="jt-inline-actions">
              <Button
                size="sm"
                variant="secondary"
                disabled={busy || !messageDirty || message.length > 280}
                onClick={() => saveSettings({ message })}
              >
                บันทึกข้อความ
              </Button>
            </div>

            <div className="jt-preview-box">
              <p className="jt-field-hint">ตัวอย่างที่ลูกค้าจะเห็น</p>
              {view?.preview.visible ? (
                <>
                  {view.preview.message && <p className="jt-preview-message">{view.preview.message}</p>}
                  <a
                    className="jt-btn"
                    data-style="primary"
                    data-size="sm"
                    data-emphasis="primary"
                    href={view.preview.addFriendUrl ?? '#'}
                    target="_blank"
                    rel="noreferrer"
                  >
                    เพิ่มเพื่อน LINE ของผู้จัด
                  </a>
                  {view.preview.source === 'event_column' && (
                    <p className="jt-field-hint">
                      ตอนนี้ใช้ลิงก์เดิมที่ตั้งไว้ก่อนหน้านี้ — เปิดสวิตช์ด้านบนเพื่อใช้ OA ที่เพิ่งตั้งค่าแทน
                    </p>
                  )}
                </>
              ) : (
                <p className="jt-field-hint">ยังไม่แสดงปุ่มนี้กับลูกค้า</p>
              )}
            </div>
          </Card>
        )
      }
      configuration={
        <>
          <Card
            title={connection ? 'ข้อมูลการเชื่อมต่อ' : 'เพิ่ม LINE OA ของผู้จัด'}
            sub={connection ? undefined : 'กรอกชื่อที่แสดงเป็นอย่างน้อย แล้วค่อยเติมส่วนที่เหลือทีหลังได้'}
          >
            <div className="jt-field-grid">
              <TextField
                label="ชื่อที่แสดงใน JamTick"
                requiredMark
                value={draft.displayName}
                onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                placeholder="เช่น Moonfield Fest OA"
              />
              <TextField
                label="LINE Basic ID"
                value={draft.basicId}
                onChange={(e) => setDraft({ ...draft, basicId: e.target.value })}
                placeholder="@moonfield"
              />
              <TextField
                label="ลิงก์เพิ่มเพื่อน"
                value={draft.addFriendUrl}
                onChange={(e) => setDraft({ ...draft, addFriendUrl: e.target.value })}
                placeholder="https://lin.ee/xxxxxxx"
              />
              {wantsMessaging && (
                <TextField
                  label="Messaging API Channel ID"
                  value={draft.messagingChannelId}
                  onChange={(e) => setDraft({ ...draft, messagingChannelId: e.target.value })}
                />
              )}
            </div>
            <Toggle
              label="ใช้ Messaging API ด้วย"
              checked={wantsMessaging}
              onChange={(e) => setWantsMessaging(e.target.checked)}
              consequence="ต้องมี channel secret และ access token — ใช้เมื่อต้องการให้ OA นี้ส่งข้อความเอง"
            />
            {!connection && (
              <div className="jt-inline-actions">
                <Button variant="primary" disabled={busy || !draft.displayName} onClick={create}>
                  บันทึก
                </Button>
              </div>
            )}
          </Card>

          {connection && wantsMessaging && (
            <Card title="Credential" sub="ระบบไม่แสดงค่าเดิมและอ่านกลับไม่ได้ ถ้าต้องเปลี่ยนให้ใส่ค่าใหม่ทับ">
              <CredentialReveal
                label="Channel secret"
                masked={connection.secretsConfigured.messagingSecret ? '•••••••• ตั้งค่าแล้ว' : 'ยังไม่ได้ตั้ง'}
                status={connection.status === 'credential_invalid' ? 'revoked' : 'active'}
                lastUsedNote={`เก็บไว้ที่ ${connection.secretSource === 'deployment' ? 'deployment' : 'ฐานข้อมูล'}`}
              />
              <CredentialReveal
                label="Channel access token"
                masked={connection.secretsConfigured.messagingAccessToken ? '•••••••• ตั้งค่าแล้ว' : 'ยังไม่ได้ตั้ง'}
                status={connection.status === 'credential_invalid' ? 'revoked' : 'active'}
              />
              <div className="jt-field-grid">
                <TextField
                  label="Channel secret ใหม่"
                  type="password"
                  autoComplete="off"
                  value={secrets.messagingChannelSecret}
                  onChange={(e) => setSecrets({ ...secrets, messagingChannelSecret: e.target.value })}
                />
                <TextField
                  label="Channel access token ใหม่"
                  type="password"
                  autoComplete="off"
                  value={secrets.messagingAccessToken}
                  onChange={(e) => setSecrets({ ...secrets, messagingAccessToken: e.target.value })}
                />
              </div>
              <div className="jt-inline-actions">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy || (!secrets.messagingChannelSecret && !secrets.messagingAccessToken)}
                  onClick={rotate}
                >
                  บันทึก secret
                </Button>
              </div>
            </Card>
          )}

          {token && (connection?.capability === 'messaging' ? (
            <LineSalesReportPanel token={token} eventSlug={eventSlug} connection={connection} permitted={permitted} />
          ) : (
            <Card title="รายงานยอดขายเข้า LINE group" sub="ต้องใช้ LINE OA ที่เปิด Messaging API">
              <p className="jt-field-hint">เปิด Messaging API และตรวจสอบการเชื่อมต่อก่อน จึงจะจับคู่กลุ่มและตั้งเวลาส่งรายงานได้</p>
            </Card>
          ))}
        </>
      }
      audit={
        connection && (
          <>
            <Card title="ผลการตรวจสอบ" sub={report ? undefined : 'กด “ตรวจสอบการเชื่อมต่อ” เพื่อดูผลล่าสุด'}>
              {report ? (
                <>
                  <ul className="jt-checks">
                    {report.checks.map((check) => (
                      <li key={check.id} className="jt-check" data-status={check.status}>
                        <span className="jt-check-mark" aria-hidden="true">{CHECK_MARK[check.status]}</span>
                        <span className="jt-check-copy">
                          <span className="jt-check-label">
                            {CHECK_LABEL[check.id]}
                            <span className="jt-sr-only">
                              {' — '}
                              {check.status === 'passed' ? 'ผ่าน'
                                : check.status === 'failed' ? 'ไม่ผ่าน'
                                  : check.status === 'skipped' ? 'ข้าม' : 'ตรวจอัตโนมัติไม่ได้'}
                            </span>
                          </span>
                          {check.detail && <span className="jt-check-detail">{check.detail}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="jt-field-hint">
                    “–” คือข้ามเพราะไม่เกี่ยวกับการตั้งค่าแบบนี้ และ “?” คือยังตรวจอัตโนมัติไม่ได้ — ทั้งสองอย่างไม่ใช่ข้อผิดพลาด
                  </p>
                </>
              ) : (
                <p className="jt-field-hint">ยังไม่มีผลตรวจสอบในรอบนี้</p>
              )}
            </Card>
            <Card title="ประวัติการเปลี่ยนแปลง" sub="ใครเปลี่ยนอะไรกับการเชื่อมต่อนี้">
              {audit === null
                ? <p className="jt-field-hint">กำลังโหลดประวัติ…</p>
                : <AuditTimeline label="ประวัติการเปลี่ยนแปลง LINE OA" events={auditEvents(audit)} />}
            </Card>
          </>
        )
      }
      dangerZone={
        connection && dirty ? (
          <SaveBar
            state={saveState}
            error={error || null}
            onSave={save}
            consequence="การแก้ไขข้อมูลการเชื่อมต่ออาจทำให้สถานะกลับไปเป็น “รอตรวจสอบ” จนกว่าจะตรวจผ่านอีกครั้ง"
          />
        ) : undefined
      }
    />
  );
}

export function LineOaDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useLineOa(token, eventSlug);
  return <LineOaScreen destination={destination} state={state} permitted={permitted} role={role} token={token} eventSlug={eventSlug} />;
}
